import { EntryWithID, LimitPagination } from '@/cms/types/entry';

import {
  ArticleItem,
  transformBlogArticlesIntoRelatedArticlesProps,
} from './service';

export const ARTICLES_PER_PAGE = 18;

export interface State {
  loading: boolean;
  articles: ArticleItem[];
  pagination: LimitPagination;
  query?: string;
  topic?: string;
  offset: number;
}

interface LoadingAction {
  type: 'loading_articles';
}

interface ErrorAction {
  type: 'no_articles';
  payload: {
    topic?: string;
    query?: string;
  };
}

interface FilteredArticlesAction {
  type: 'filtered_articles';
  payload: {
    articles: {
      data: EntryWithID[];
      pagination: LimitPagination;
    };
    topic: string;
  };
}
interface SearchedArticlesAction {
  type: 'searched_articles';
  payload: {
    articles: {
      data: EntryWithID[];
      pagination: LimitPagination;
    };
    query: string;
  };
}
interface ChangedPageAction {
  type: 'changed_page';
}

interface ResetPageAction {
  type: 'reset_page';
}

export type Action =
  | LoadingAction
  | ErrorAction
  | ChangedPageAction
  | ResetPageAction
  | FilteredArticlesAction
  | SearchedArticlesAction;

export function reducer(state: State, action: Action): State {
  if (action.type === 'loading_articles') {
    return {
      ...state,
      loading: true,
    };
  }
  if (action.type === 'no_articles') {
    return {
      articles: [],
      offset: 0,
      pagination: {
        limit: 0,
        start: 0,
        total: 0,
      },
      query: action.payload.query,
      topic: action.payload.topic,
      loading: false,
    };
  }
  if (action.type === 'filtered_articles') {
    const newArticles = transformBlogArticlesIntoRelatedArticlesProps(
      action.payload.articles.data
    );
    return {
      ...state,
      loading: false,
      articles: newArticles,
      pagination: action.payload.articles.pagination,
      topic: action.payload.topic,
    };
  }
  if (action.type === 'searched_articles') {
    const newArticles = transformBlogArticlesIntoRelatedArticlesProps(
      action.payload.articles.data
    );
    return {
      ...state,
      loading: false,
      articles: newArticles,
      pagination: action.payload.articles.pagination,
      query: action.payload.query,
    };
  }
  if (action.type === 'changed_page') {
    return {
      ...state,
      offset: state.offset + ARTICLES_PER_PAGE,
    };
  }
  if (action.type === 'reset_page') {
    return {
      ...state,
      query: undefined,
      topic: undefined,
      offset: 0,
    };
  }
  throw Error(`Unknown action: ${action}`);
}

export function generateInitialState({
  articles,
  topic,
}: {
  articles?: {
    data: EntryWithID[];
    pagination: LimitPagination;
  };
  topic: string | null;
}): State {
  return {
    loading: false,
    offset: articles?.pagination ? articles?.pagination.start : 0,
    articles: articles?.data
      ? transformBlogArticlesIntoRelatedArticlesProps(articles?.data)
      : [],
    pagination: articles?.pagination ?? {
      limit: ARTICLES_PER_PAGE,
      start: 0,
      total: 0,
    },
    topic: topic ?? undefined,
  };
}
